//@ts-check
/**
 * OPFS cache helpers for RCS point cloud data.
 *
 * Provides bucket naming, hash key strings, cache parameter validation,
 * and serialization utilities used by {@link RcsOpfsCacheClient} and
 * {@link LoadRcsNodeTask} to persist and retrieve RCS data from OPFS.
 */

import {
  RCS_CACHE_VERSION, LOD_MAPPING_MODE, POINTS_PER_LEVEL,
  RCS_HEADER_BYTES, RCS_HEADER_MAGIC, UMBRELLA_LEAF_RECORD_BYTES, OCTREE_CHUNK_LEAF_COUNT_BYTES,
  MIN_LEVELS_IN_LOD0, RCS_LOD0_TARGET_MAX_BYTES } from
'./RcsConstants.js';
// @ts-ignore TS5097 — the explicit .ts extension is required so the ES6-module import map can
// resolve this TypeScript-authored module; tsc rejects .ts import paths unless
// allowImportingTsExtensions is enabled (which conflicts with this project's declaration emit).
import { makeFilenameSafe, RCS_CACHE_DIRECTORY } from '../lmvtk/otg/OPFSCache.ts';

/**
 * Cache schema version and algorithm parameters persisted in OPFS and validated on warm
 * load. A mismatch invalidates the cached blobs, i.e. the buckets are deleted and refilled.
 *
 * @typedef {Object} RcsCacheParams
 * @property {number} cacheVersion - Value of {@link RCS_CACHE_VERSION} at store time.
 * @property {string} lodMappingMode - Value of {@link LOD_MAPPING_MODE} at store time.
 * @property {number} pointsPerLevel - Value of {@link POINTS_PER_LEVEL} at store time.
 * @property {number} minLevelsInLod0 - Value of {@link MIN_LEVELS_IN_LOD0} at store time.
 * @property {number} lod0TargetMaxBytes - Value of {@link RCS_LOD0_TARGET_MAX_BYTES} at store time.
 */

// ── Bucket naming ──────────────────────────────────────────────────────

/**
 * OPFS directory (under the origin storage root) holding all RCS cache buckets.
 *
 * Re-exported from `OPFSCache`, which owns the list of cache directories so quota-induced
 * eviction can see all of them.
 * @type {string}
 */
export const RCS_OPFS_CACHE_DIR = RCS_CACHE_DIRECTORY;

/**
 * Filesystem-safe bucket-name stem for a model.
 *
 * Rejects a missing identifier rather than stringifying it: `String(null)` would map every such
 * model onto one shared `rcs_null_*` bucket, mixing unrelated models' blobs under keys that are
 * themselves derived from the same missing identifier. Callers gate on a resolved cache id, so
 * this is a guard against a future caller forgetting to.
 *
 * @param {string|null} urn - Model cache identifier (Forge URN or derived URL hash)
 * @returns {string}
 */
function bucketStem(urn) {
  if (!urn) {
    throw new Error('RCS OPFS bucket name requires a model cache identifier');
  }
  return makeFilenameSafe(urn);
}

/**
 * OPFS bucket name for header, octree, and inner-node data for a model.
 *
 * The bucket name is version-free on purpose: a {@link RCS_CACHE_VERSION} bump is detected
 * through the params blob (see {@link validateCacheParams}), which deletes the bucket instead
 * of leaving an unreachable one behind until eviction.
 *
 * @param {string|null} urn - Model cache identifier (Forge URN or derived URL hash)
 * @returns {string} Filesystem-safe bucket name
 */
export function getDataBucketName(urn) {
  return `rcs_${bucketStem(urn)}_data`;
}

/**
 * OPFS bucket name for LOD-0 decoded pull-up results for a model.
 *
 * @param {string|null} urn - Model cache identifier (Forge URN or derived URL hash)
 * @returns {string} Filesystem-safe bucket name
 */
export function getLod0BucketName(urn) {
  return `rcs_${bucketStem(urn)}_lod0`;
}

// ── Hash key strings (hashed to OPFS keys in the fetch worker) ─────────

/**
 * Logical cache key for the algorithm-params sentinel blob.
 * @param {string|null} urn - Model cache identifier
 * @returns {string} Key string passed to {@link digestToPackedString} in the worker
 */
export function paramsHashKey(urn) {return `${urn}:params`;}

/**
 * Logical cache key for the per-model LOD-0 source level count chosen at first load.
 * @param {string|null} urn - Model cache identifier
 * @returns {string} Key string passed to {@link digestToPackedString} in the worker
 */
export function levelsInLod0HashKey(urn) {return `${urn}:levelsInLod0`;}

/**
 * Logical cache key for the raw RCS file header bytes.
 * @param {string|null} urn - Model cache identifier
 * @returns {string} Key string passed to {@link digestToPackedString} in the worker
 */
export function headerHashKey(urn) {return `${urn}:header`;}

/**
 * Logical cache key for the raw umbrella octree chunk (chunk 2) bytes.
 * @param {string|null} urn - Model cache identifier
 * @returns {string} Key string passed to {@link digestToPackedString} in the worker
 */
export function octreeHashKey(urn) {return `${urn}:octree`;}

/**
 * Logical cache key for an inner BVH node's interleaved vertex buffer.
 * @param {string|null} urn - Model cache identifier
 * @param {number} bvhNodeId - Inner BVH node id (also the geometry's node id)
 * @returns {string} Key string passed to {@link digestToPackedString} in the worker
 */
export function innerNodeHashKey(urn, bvhNodeId) {return `${urn}:inner:${bvhNodeId}`;}

/**
 * Logical cache key for a leaf LOD fragment's raw SVO payload bytes (LOD ≥ 1).
 * @param {string|null} urn - Model cache identifier
 * @param {number} umbrellaIndex - Index into the umbrella leaf array
 * @param {number} lodLevel - LOD level (must be ≥ 1; LOD-0 uses {@link lod0HashKey})
 * @returns {string} Key string passed to {@link digestToPackedString} in the worker
 */
export function leafHashKey(urn, umbrellaIndex, lodLevel) {return `${urn}:leaf:${umbrellaIndex}:${lodLevel}`;}

/**
 * Logical cache key for a LOD-0 umbrella leaf's raw (undecoded) SVO payload bytes.
 * Stored in the transient LOD-0 bucket ({@link getLod0BucketName}) so an interrupted
 * cold load can be resumed without re-fetching; the bucket is deleted once all inner
 * nodes are cached.
 * @param {string|null} urn - Model cache identifier
 * @param {number} umbrellaIndex - Index into the umbrella leaf array
 * @returns {string} Key string passed to {@link digestToPackedString} in the worker
 */
export function lod0HashKey(urn, umbrellaIndex) {return `${urn}:lod0:${umbrellaIndex}`;}

/**
 * Logical cache key for a LOD-0 umbrella leaf's decoded fall-through (overflow) points:
 * the points that did not fit into any ancestor inner node and remain on the leaf itself.
 * Stored in the retained data bucket ({@link getDataBucketName}) so warm reloads can render
 * them after the transient LOD-0 bucket has been deleted.
 * @param {string|null} urn - Model cache identifier
 * @param {number} umbrellaIndex - Index into the umbrella leaf array
 * @returns {string} Key string passed to {@link digestToPackedString} in the worker
 */
export function leafOverflowHashKey(urn, umbrellaIndex) {return `${urn}:leafoverflow:${umbrellaIndex}`;}

// ── Raw blob validation ────────────────────────────────────────────────

/**
 * Return whether `data` looks like a valid raw RCS file header blob.
 * Accepts buffers at least {@link RCS_HEADER_BYTES} long; longer buffers are
 * tolerated (e.g. read-ahead) but only the fixed header size is required.
 *
 * The magic is checked as well as the length, because the caller parses the header outside any
 * per-blob error handling: a right-sized blob with wrong content would fail the load without
 * invalidating the bucket, and would then fail identically on every subsequent reload.
 *
 * @param {Uint8Array|null|undefined} data - Candidate header bytes from OPFS or the network.
 * @returns {boolean} True when `data` is present, large enough to parse, and starts with the RCS magic.
 */
export function validateHeaderBytes(data) {
  if (!data || data.byteLength < RCS_HEADER_BYTES) return false;
  for (let i = 0; i < RCS_HEADER_MAGIC.length; i++) {
    if (data[i] !== RCS_HEADER_MAGIC.charCodeAt(i)) return false;
  }
  return true;
}

/**
 * Return whether `data` matches the on-disk layout of umbrella octree chunk 2:
 * an 8-byte little-endian leaf count followed by one fixed-size leaf record per leaf.
 *
 * @param {Uint8Array|null|undefined} data - Raw chunk-2 bytes from OPFS or the network.
 * @returns {boolean} True when the buffer length equals
 *   `{@link OCTREE_CHUNK_LEAF_COUNT_BYTES} + numLeafs * {@link UMBRELLA_LEAF_RECORD_BYTES}`.
 */
export function validateOctreeChunkBytes(data) {
  if (!data || data.byteLength < OCTREE_CHUNK_LEAF_COUNT_BYTES) return false;
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const numLeafsLow = view.getUint32(0, true);
  const numLeafsHigh = view.getUint32(4, true);
  const numLeafs = numLeafsLow + numLeafsHigh * 4294967296;
  return data.byteLength === OCTREE_CHUNK_LEAF_COUNT_BYTES + numLeafs * UMBRELLA_LEAF_RECORD_BYTES;
}

// ── JSON blob helpers ──────────────────────────────────────────────────

/**
 * Encode a value as a UTF-8 JSON blob for OPFS storage.
 * @param {unknown} value
 * @returns {Uint8Array}
 */
function encodeJson(value) {
  return new TextEncoder().encode(JSON.stringify(value));
}

/**
 * Decode a UTF-8 JSON blob, returning null on absent or malformed data.
 * @param {Uint8Array|null|undefined} data - Serialized JSON bytes from OPFS.
 * @returns {unknown} Parsed value, or null when `data` is missing or invalid JSON.
 */
function decodeJson(data) {
  if (!data) return null;
  try {
    return JSON.parse(new TextDecoder().decode(data));
  } catch {
    return null;
  }
}

// ── Cache parameters ───────────────────────────────────────────────────

/**
 * Build the current cache version and algorithm parameters object. Stored in OPFS so a
 * mismatch invalidates cached blobs when the cache schema, LOD mapping, or point
 * distribution changes.
 *
 * @returns {RcsCacheParams}
 */
export function getCacheParams() {
  return {
    cacheVersion: RCS_CACHE_VERSION,
    lodMappingMode: LOD_MAPPING_MODE,
    pointsPerLevel: POINTS_PER_LEVEL,
    minLevelsInLod0: MIN_LEVELS_IN_LOD0,
    lod0TargetMaxBytes: RCS_LOD0_TARGET_MAX_BYTES
  };
}

/**
 * Parse algorithm-parameter bytes previously written by {@link serializeCacheParams}.
 *
 * @param {Uint8Array|null|undefined} data - Params blob from OPFS, or absent on cold load.
 * @returns {RcsCacheParams|null} Parsed params, or null when `data` is missing or malformed.
 */
export function parseCacheParams(data) {
  const cached = decodeJson(data);
  if (!cached || typeof cached !== 'object') return null;
  return /** @type {RcsCacheParams} */cached;
}

/**
 * Validate cached params against the parameters the current session loads with.
 *
 * The fetch worker passes the main thread's parameters explicitly: some of them can be
 * overridden at runtime (see `setRcsLod0TargetMaxBytes`), and the worker holds its
 * own module instance of those constants, so recomputing them there would compare against
 * the defaults instead of the values the model is actually loaded with.
 *
 * @param {Uint8Array|null|undefined} data - Serialized params blob read from OPFS, or absent on cold load
 * @param {RcsCacheParams} [current] - Parameters to compare against; defaults to this context's
 * @returns {boolean}
 */
export function validateCacheParams(data, current = getCacheParams()) {
  const cached = parseCacheParams(data);
  if (!cached) return false;
  return cached.cacheVersion === current.cacheVersion &&
  cached.lodMappingMode === current.lodMappingMode &&
  cached.pointsPerLevel === current.pointsPerLevel &&
  cached.minLevelsInLod0 === current.minLevelsInLod0 &&
  cached.lod0TargetMaxBytes === current.lod0TargetMaxBytes;
}

/**
 * Serialize {@link getCacheParams} to a UTF-8 JSON blob for OPFS storage.
 * @returns {Uint8Array}
 */
export function serializeCacheParams() {
  return encodeJson(getCacheParams());
}

/**
 * Serialize the per-model LOD-0 source level count for OPFS storage.
 *
 * @param {number} directModeLevelsInLod0 - Count of coarsest source levels folded into LOD 0
 *   (from {@link ./RcsLodMapping.js#computeDirectModeLevelsInLod0}).
 * @returns {Uint8Array} UTF-8 JSON blob `{ directModeLevelsInLod0 }`.
 */
export function serializeLevelsInLod0(directModeLevelsInLod0) {
  return encodeJson({ directModeLevelsInLod0 });
}

/**
 * Deserialize the per-model LOD-0 source level count from OPFS.
 *
 * @param {Uint8Array|null|undefined} data - Blob previously written by {@link serializeLevelsInLod0}.
 * @returns {number|null} Stored level count, or null when `data` is missing or malformed.
 */
export function deserializeLevelsInLod0(data) {
  const obj = decodeJson(data);
  if (!obj || typeof obj !== 'object') return null;
  const { directModeLevelsInLod0 } = /** @type {{ directModeLevelsInLod0?: unknown }} */obj;
  return typeof directModeLevelsInLod0 === 'number' ? directModeLevelsInLod0 : null;
}

// ── Point data serialization (inner nodes only) ────────────────────────

/**
 * Serialize a Float32Array vertex buffer to a Uint8Array for OPFS storage.
 * @param {Float32Array} float32Array - Interleaved XYZRGB vertex buffer (6 floats per point)
 * @returns {Uint8Array} Byte copy suitable for transfer to the fetch worker
 */
export function serializePointData(float32Array) {
  return new Uint8Array(float32Array.buffer.slice(float32Array.byteOffset, float32Array.byteOffset + float32Array.byteLength));
}

/**
 * Deserialize a Uint8Array back to a Float32Array vertex buffer.
 *
 * The zero-copy view requires `byteOffset` to be a multiple of 4, which holds because every
 * blob on this path comes from `OPFSCache.get`, which allocates a fresh zero-offset buffer per
 * entry. Feeding it a sub-view of a larger buffer would throw a `RangeError`.
 *
 * @param {Uint8Array} uint8Array - Blob previously written by {@link serializePointData}
 * @returns {Float32Array} Interleaved XYZRGB vertex buffer
 */
export function deserializePointData(uint8Array) {
  return new Float32Array(uint8Array.buffer, uint8Array.byteOffset, uint8Array.byteLength / 4);
}

/**
 * Wait until `getPending()` reports no outstanding promises, or until `timeoutMs` elapses.
 *
 * Re-reads the set on every iteration because settling one promise can enqueue another (a
 * flush scheduled during teardown, say), and callers need the queue empty rather than merely
 * one generation older. The deadline keeps a job that never settles from blocking teardown.
 *
 * @param {() => Iterable<Promise<unknown>> & {size: number}} getPending - Live view of the pending set
 * @param {number} timeoutMs - Overall budget across all iterations
 * @returns {Promise<boolean>} True if the set drained, false if the timeout was hit
 */
export async function drainPendingPromises(getPending, timeoutMs) {
  const deadline = Date.now() + timeoutMs;
  while (getPending().size > 0) {
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      return false;
    }
    /** @type {ReturnType<typeof setTimeout>|undefined} */
    let timer;
    const expiry = new Promise((resolve) => {timer = setTimeout(resolve, remaining);});
    await Promise.race([Promise.allSettled([...getPending()]), expiry]);
    clearTimeout(timer);
  }
  return true;
}